/**
 * State Management Module
 * Centralized state store for dashboard data
 */

class DashboardState {
    constructor() {
        this.state = {
            user: null,
            renderedUser: null,
            rawData: {
                scale: [],
                tape: [],
                meds: [],
                photos: [],
                body_measurements: [],
                activity: [],
                sleep: []
            },
            activeChart: null,
            loading: {},
            errors: {}
        };
        this.listeners = new Map();
    }

    /**
     * Get current state
     * @returns {Object} Current state
     */
    getState() {
        return this.state;
    }

    /**
     * Set user data
     * @param {Object} user - User object
     */
    setUser(user) {
        this.state.user = user;
        this.notify('user', user);
    }

    /**
     * Set rendered user (for impersonation)
     * @param {Object} user - Rendered user object
     */
    setRenderedUser(user) {
        this.state.renderedUser = user;
        this.notify('renderedUser', user);
    }

    /**
     * Set raw data for a module
     * @param {string} module - Module name
     * @param {Array} data - Module data
     */
    setRawData(module, data) {
        this.state.rawData[module] = data;
        this.notify(`rawData.${module}`, data);
    }

    /**
     * Get raw data for a module
     * @param {string} module - Module name
     * @returns {Array} Module data
     */
    getRawData(module) {
        return this.state.rawData[module] || [];
    }

    /**
     * Set loading state for a module
     * @param {string} module - Module name
     * @param {boolean} isLoading - Loading state
     */
    setLoading(module, isLoading) {
        this.state.loading[module] = isLoading;
        this.notify(`loading.${module}`, isLoading);
    }

    /**
     * Set error for a module
     * @param {string} module - Module name
     * @param {Error|null} error - Error object
     */
    setError(module, error) {
        this.state.errors[module] = error;
        this.notify(`error.${module}`, error);
    }

    /**
     * Subscribe to state changes
     * @param {string} key - State key to watch
     * @param {Function} callback - Callback function
     * @returns {Function} Unsubscribe function
     */
    subscribe(key, callback) {
        if (!this.listeners.has(key)) {
            this.listeners.set(key, []);
        }
        this.listeners.get(key).push(callback);

        // Return unsubscribe function
        return () => {
            const callbacks = this.listeners.get(key);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }

    /**
     * Notify listeners of state change
     * @param {string} key - State key that changed
     * @param {*} value - New value
     */
    notify(key, value) {
        const callbacks = this.listeners.get(key) || [];
        callbacks.forEach(callback => callback(value));
    }

    /**
     * Clear all state
     */
    clear() {
        this.state = {
            user: null,
            renderedUser: null,
            rawData: {
                scale: [],
                tape: [],
                meds: [],
                photos: [],
                body_measurements: [],
                activity: [],
                sleep: []
            },
            activeChart: null,
            loading: {},
            errors: {}
        };
        this.notify('clear', null);
    }
}

// Export singleton instance
export const dashboardState = new DashboardState();

// Export class for testing
export { DashboardState };
